import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { TableauDefilant } from '../../../../components/TableauDefilant.js';
import type { CiblesPivot, FiltreRegle, Regles } from '../../../../lib/correspondance.js';
import tableStyles from '../../../../styles/table.module.css';
import reprise from '../Reprise.module.css';
import { libelleRef, libelleTransformation } from './libelles.js';
import styles from './Correspondance.module.css';

/**
 * Les règles d'un profil, en lecture : les fichiers qu'elles lisent, puis,
 * nature par nature, ce qui alimente chaque champ et ce qu'on lui fait
 * subir. Une version est immuable : cet écran ne propose aucune édition, qui
 * se fait depuis un lot.
 */
export function ReglesLecture({
  regles,
  cibles,
}: {
  regles: Regles;
  cibles: CiblesPivot | undefined;
}): JSX.Element {
  const { t } = useTranslation('console');
  const filtre = (f: FiltreRegle): string =>
    `${libelleRef(t, f.source)} ${t(`correspondance.generales.operateurs.${f.operateur}`).toLowerCase()}${
      f.operateur === 'egal' || f.operateur === 'different' ? ` « ${f.valeur} »` : ''
    }`;

  return (
    <div className={reprise.pile}>
      <div className={reprise.pile}>
        <h4 className={reprise.sousTitre}>{t('correspondance.generales.sources')}</h4>
        <ul className={styles.listeSimple}>
          {regles.sources.map((source) => (
            <li key={source.role}>
              {[source.role, source.feuille ?? source.racine].filter(Boolean).join(' · ')}
            </li>
          ))}
        </ul>
        {regles.jointures.length > 0 ? (
          <ul className={styles.listeSimple}>
            {regles.jointures.map((jointure) => (
              <li key={jointure.role}>
                {t('correspondance.lecture.jointure', {
                  role: jointure.role,
                  cle: jointure.cle,
                  sur: jointure.sur,
                  mode: t(`correspondance.generales.modes.${jointure.mode}`),
                })}
              </li>
            ))}
          </ul>
        ) : null}
        {regles.deplier ? (
          <p className={reprise.discret}>
            {t('correspondance.lecture.deplier', { chemin: regles.deplier.chemin })}
          </p>
        ) : null}
        {regles.filtres.length > 0 ? (
          <p className={reprise.discret}>
            {t('correspondance.lecture.filtres', {
              filtres: regles.filtres.map(filtre).join(' ; '),
            })}
          </p>
        ) : null}
      </div>

      {Object.entries(regles.natures).map(([nature, bloc]) => {
        const colonnes = cibles?.natures.find((cible) => cible.nature === nature)?.colonnes ?? [];
        return (
          <div key={nature} className={reprise.pile}>
            <h4 className={reprise.sousTitre}>{t(`reprise.natures.${nature}`)}</h4>
            <TableauDefilant
              libelle={t('correspondance.lecture.tableau', {
                nature: t(`reprise.natures.${nature}`),
              })}
              empile
            >
              <table className={`${tableStyles.table} ${reprise.table}`}>
                <thead>
                  <tr>
                    <th scope="col">{t('correspondance.lecture.champ')}</th>
                    <th scope="col">{t('correspondance.lecture.source')}</th>
                    <th scope="col">{t('correspondance.lecture.transformations')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(bloc.colonnes).map(([cle, champ]) => (
                    <tr key={cle}>
                      <td data-label={t('correspondance.lecture.champ')}>
                        {colonnes.find((colonne) => colonne.cle === cle)?.entete ?? cle}
                      </td>
                      <td data-label={t('correspondance.lecture.source')}>
                        {champ.sources.length === 0
                          ? '—'
                          : champ.sources
                              .map((ref) => libelleRef(t, ref))
                              .join(` ${champ.separateur.trim() || '+'} `)}
                      </td>
                      <td data-label={t('correspondance.lecture.transformations')}>
                        {champ.transformations.length === 0
                          ? '—'
                          : champ.transformations
                              .map((etape) => libelleTransformation(t, etape))
                              .join(' → ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableauDefilant>
            {bloc.cleOrigine ? (
              <p className={reprise.discret}>
                {t('correspondance.lecture.cleOrigine', { cle: libelleRef(t, bloc.cleOrigine) })}
              </p>
            ) : null}
            {bloc.filtres.length > 0 ? (
              <p className={reprise.discret}>
                {t('correspondance.lecture.filtres', {
                  filtres: bloc.filtres.map(filtre).join(' ; '),
                })}
              </p>
            ) : null}
            {bloc.dedoublonner ? (
              <p className={reprise.discret}>
                {t('correspondance.lecture.dedoublonner', { champ: bloc.dedoublonner })}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
